import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #fff;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 60px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 80px 24px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 10px;
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 40px;
  text-align: center;
  line-height: 1.6;
`;

const DenominationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const DenominationButton = styled.button`
  padding: 20px;
  border: 2px solid ${props => props.selected ? '#000' : '#e0e0e0'};
  background: ${props => props.selected ? '#000' : '#fff'};
  color: ${props => props.selected ? '#fff' : '#000'};
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #000;
    transform: translateY(-2px);
  }
`;

const CustomAmountInput = styled.div`
  margin-top: 24px;
`;

const InfoBox = styled.div`
  background: #f5f5f5;
  padding: 20px;
  border-radius: 8px;
  margin-top: 24px;
`;

const InfoTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const InfoText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  margin-bottom: 8px;
`;

const GiftCardPurchasePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [recipientEmail, setRecipientEmail] = useState(user?.email || '');
  const [isProcessing, setIsProcessing] = useState(false);

  const denominations = [25, 50, 100, 150, 200, 250, 300, 500];

  // Check if payment was canceled
  React.useEffect(() => {
    if (searchParams.get('canceled') === 'true') {
      toast.error('Payment was canceled');
    }
  }, [searchParams]);

  const handleDenominationClick = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    setSelectedAmount(null);
  };

  const getFinalAmount = () => {
    if (selectedAmount) return selectedAmount;
    if (customAmount) {
      const amount = parseFloat(customAmount);
      if (amount > 0 && amount <= 500) return amount;
    }
    return null;
  };

  const handlePurchase = async () => {
    const amount = getFinalAmount();

    if (!amount) {
      toast.error('Please select or enter a gift card amount');
      return;
    }

    if (amount > 500) {
      toast.error('Gift card amount cannot exceed £500');
      return;
    }

    if (amount <= 0) {
      toast.error('Gift card amount must be greater than £0');
      return;
    }

    if (!recipientEmail || !recipientEmail.includes('@')) {
      toast.error('Please enter a valid recipient email address');
      return;
    }

    setIsProcessing(true);

    try {
      const response = await api.post('/api/giftcards/purchase', {
        amount,
        recipientEmail,
      });

      if (response.data.success && response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to initiate payment');
        setIsProcessing(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to process gift card purchase');
      setIsProcessing(false);
    }
  };

  return (
    <PageContainer>
      <NavbarSpacer />
      <ContentWrapper>
        <Title>Purchase Gift Card</Title>
        <Subtitle>
          Give the gift of style! Purchase a TheMadZone gift card that can be used on any purchase.
        </Subtitle>

        <Card>
          <CardHeader>
            <CardTitle>Select Gift Card Amount</CardTitle>
          </CardHeader>
          <CardContent>
            <DenominationsGrid>
              {denominations.map((amount) => (
                <DenominationButton
                  key={amount}
                  selected={selectedAmount === amount}
                  onClick={() => handleDenominationClick(amount)}
                >
                  £{amount}
                </DenominationButton>
              ))}
            </DenominationsGrid>

            <CustomAmountInput>
              <Label htmlFor="customAmount">Or Enter Custom Amount (Max £500)</Label>
              <Input
                id="customAmount"
                type="number"
                min="1"
                max="500"
                step="0.01"
                value={customAmount}
                onChange={handleCustomAmountChange}
                placeholder="Enter amount"
                style={{ marginTop: '8px' }}
              />
            </CustomAmountInput>

            {getFinalAmount() && (
              <div style={{ marginTop: '24px', padding: '16px', background: '#f0f9ff', borderRadius: '8px' }}>
                <p style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                  Total: £{getFinalAmount().toFixed(2)}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card style={{ marginTop: '24px' }}>
          <CardHeader>
            <CardTitle>Recipient Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ marginBottom: '16px' }}>
              <Label htmlFor="recipientEmail">Recipient Email Address</Label>
              <Input
                id="recipientEmail"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="recipient@example.com"
                style={{ marginTop: '8px' }}
              />
              <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                The gift card code will be sent to this email address after payment.
              </p>
            </div>
          </CardContent>
        </Card>

        <InfoBox>
          <InfoTitle>Gift Card Information</InfoTitle>
          <InfoText>
            • Gift cards can be used on any purchase at TheMadZone
          </InfoText>
          <InfoText>
            • Maximum gift card value: £500
          </InfoText>
          <InfoText>
            • Gift cards never expire
          </InfoText>
          <InfoText>
            • Gift card codes will be sent via email after successful payment
          </InfoText>
          <InfoText>
            • Gift cards can be used in combination with other discounts
          </InfoText>
          <InfoText style={{ color: '#dc2626', fontWeight: 600 }}>
            • <strong>IMPORTANT:</strong> Gift cards are non-refundable once the transaction is successful. All sales are final.
          </InfoText>
        </InfoBox>

        <div style={{ marginTop: '32px', display: 'flex', gap: '16px', justifyContent: 'center' }}>
          <Button
            variant="outline"
            onClick={() => navigate('/shop')}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePurchase}
            disabled={isProcessing || !getFinalAmount()}
            style={{ minWidth: '200px' }}
          >
            {isProcessing ? 'Processing...' : `Purchase £${getFinalAmount()?.toFixed(2) || '0.00'} Gift Card`}
          </Button>
        </div>
      </ContentWrapper>
    </PageContainer>
  );
};

export default GiftCardPurchasePage;

